import { useMemo, useState } from "react";
import Layout from "@/components/feature/Layout";
import { dashboardStats, urgentPayments, upcomingOrders } from "@/mocks/dashboard";

function formatCurrency(value: number, currency = "₺"): string {
  return `${value.toLocaleString("tr-TR")} ${currency}`;
}

function StatCard({
  label,
  value,
  subValue,
  change,
  changeType,
  icon,
  accent = "gold",
}: {
  label: string;
  value: string;
  subValue?: string;
  change?: number;
  changeType?: "positive" | "negative";
  icon: string;
  accent?: "gold" | "red" | "green" | "blue";
}) {
  const accentClasses = {
    gold: "from-gold-500/20 to-gold-600/10 text-gold-400",
    red: "from-red-500/20 to-red-600/10 text-red-400",
    green: "from-emerald-500/20 to-emerald-600/10 text-emerald-400",
    blue: "from-sky-500/20 to-sky-600/10 text-sky-400",
  };

  return (
    <div className="glass-card p-5">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${accentClasses[accent]} flex items-center justify-center`}>
          <i className={`${icon} text-lg`} />
        </div>
        {change !== undefined && (
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded-full ${
              changeType === "positive"
                ? "bg-emerald-500/10 text-emerald-400"
                : "bg-red-500/10 text-red-400"
            }`}
          >
            {change > 0 ? "+" : ""}
            {change}%
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-xl font-semibold text-gray-100">{value}</p>
      {subValue && <p className="text-xs text-gray-500 mt-1">{subValue}</p>}
    </div>
  );
}

export default function Home() {
  const [activeTab, setActiveTab] = useState<"payments" | "orders">("payments");

  const stats = useMemo(() => [
    {
      label: dashboardStats.todaysCollection.label,
      value: formatCurrency(dashboardStats.todaysCollection.value),
      change: dashboardStats.todaysCollection.change,
      changeType: dashboardStats.todaysCollection.changeType,
      icon: "ri-coins-line",
      accent: "gold" as const,
    },
    {
      label: dashboardStats.upcomingPayments.label,
      value: formatCurrency(dashboardStats.upcomingPayments.value),
      subValue: `${dashboardStats.upcomingPayments.count} müşteri`,
      icon: "ri-calendar-check-line",
      accent: "blue" as const,
    },
    {
      label: dashboardStats.overdueDebts.label,
      value: formatCurrency(dashboardStats.overdueDebts.value),
      subValue: `${dashboardStats.overdueDebts.count} müşteri`,
      change: dashboardStats.overdueDebts.change,
      changeType: dashboardStats.overdueDebts.changeType,
      icon: "ri-error-warning-line",
      accent: "red" as const,
    },
    {
      label: dashboardStats.weeklyOrders.label,
      value: `${dashboardStats.weeklyOrders.value}`,
      subValue: `Bu hafta teslim`,
      icon: "ri-gift-line",
      accent: "green" as const,
    },
    {
      label: dashboardStats.totalReceivable.label,
      value: formatCurrency(dashboardStats.totalReceivable.value),
      icon: "ri-hand-coin-line",
      accent: "gold" as const,
    },
    {
      label: dashboardStats.totalPayable.label,
      value: formatCurrency(dashboardStats.totalPayable.value),
      icon: "ri-wallet-3-line",
      accent: "red" as const,
    },
    {
      label: dashboardStats.netCash.label,
      value: formatCurrency(dashboardStats.netCash.value),
      change: dashboardStats.netCash.change,
      changeType: dashboardStats.netCash.changeType,
      icon: "ri-bank-card-line",
      accent: "green" as const,
    },
  ], []);

  return (
    <Layout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-100 mb-1">Dashboard</h1>
        <p className="text-sm text-gray-500">İşletmenizin genel durumunu ve önemli metrikleri görün.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4 mb-8">
        {stats.map((stat, idx) => (
          <StatCard key={idx} {...stat} />
        ))}
      </div>

      {/* Tab Section */}
      <div className="glass-card">
        <div className="flex items-center gap-1 p-2 border-b border-white/5">
          <button
            onClick={() => setActiveTab("payments")}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all cursor-pointer ${
              activeTab === "payments"
                ? "bg-gold-500/10 text-gold-400"
                : "text-gray-400 hover:text-gray-200"
            }`}
          >
            <span className="flex items-center gap-2">
              <i className="ri-error-warning-line" />
              Acil Ödemeler ({urgentPayments.length})
            </span>
          </button>
          <button
            onClick={() => setActiveTab("orders")}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all cursor-pointer ${
              activeTab === "orders"
                ? "bg-gold-500/10 text-gold-400"
                : "text-gray-400 hover:text-gray-200"
            }`}
          >
            <span className="flex items-center gap-2">
              <i className="ri-gift-line" />
              Yaklaşan Siparişler ({upcomingOrders.length})
            </span>
          </button>
        </div>

        <div className="p-4">
          {activeTab === "payments" ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left text-xs text-gray-500 font-medium px-3 py-3">Müşteri</th>
                    <th className="text-left text-xs text-gray-500 font-medium px-3 py-3">Telefon</th>
                    <th className="text-right text-xs text-gray-500 font-medium px-3 py-3">Tutar</th>
                    <th className="text-left text-xs text-gray-500 font-medium px-3 py-3">Son Tarih</th>
                    <th className="text-left text-xs text-gray-500 font-medium px-3 py-3">Durum</th>
                    <th className="text-right text-xs text-gray-500 font-medium px-3 py-3">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {urgentPayments.map((payment) => (
                    <tr key={payment.id} className="table-row-hover border-b border-white/5 last:border-0">
                      <td className="px-3 py-3 text-sm text-gray-200 font-medium">{payment.customer}</td>
                      <td className="px-3 py-3 text-sm text-gray-400">{payment.phone}</td>
                      <td className="px-3 py-3 text-sm text-gray-200 text-right font-medium">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-400">{payment.dueDate}</td>
                      <td className="px-3 py-3">
                        <span
                          className={`status-badge ${
                            payment.status === "Gecikti"
                              ? "bg-red-500/10 text-red-400"
                              : "bg-gold-500/10 text-gold-400"
                          }`}
                        >
                          {payment.status}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <a
                            href={`https://wa.me/${payment.phone.replace(/\s/g, "")}?text=Merhaba%20${encodeURIComponent(payment.customer)},%20kuyum%20alışverişinizle%20ilgili%20kalan%20ödemenizin%20tarihi%20yaklaşmıştır.%20Müsait%20olduğunuzda%20bizimle%20iletişime%20geçmenizi%20rica%20ederiz.`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors cursor-pointer"
                            title="WhatsApp Gönder"
                          >
                            <i className="ri-whatsapp-line" />
                          </a>
                          <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 text-gray-400 hover:bg-white/10 transition-colors cursor-pointer" title="Düzenle">
                            <i className="ri-edit-line" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left text-xs text-gray-500 font-medium px-3 py-3">Müşteri</th>
                    <th className="text-left text-xs text-gray-500 font-medium px-3 py-3">Ürün</th>
                    <th className="text-left text-xs text-gray-500 font-medium px-3 py-3">Teslim Tarihi</th>
                    <th className="text-left text-xs text-gray-500 font-medium px-3 py-3">Durum</th>
                    <th className="text-right text-xs text-gray-500 font-medium px-3 py-3">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {upcomingOrders.map((order) => (
                    <tr key={order.id} className="table-row-hover border-b border-white/5 last:border-0">
                      <td className="px-3 py-3 text-sm text-gray-200 font-medium">{order.customer}</td>
                      <td className="px-3 py-3 text-sm text-gray-400">{order.product}</td>
                      <td className="px-3 py-3 text-sm text-gray-400">{order.dueDate}</td>
                      <td className="px-3 py-3">
                        <span
                          className={`status-badge ${
                            order.status === "Teslime Hazır"
                              ? "bg-emerald-500/10 text-emerald-400"
                              : order.status === "Atölyede"
                              ? "bg-sky-500/10 text-sky-400"
                              : order.status === "Hazırlanıyor"
                              ? "bg-gold-500/10 text-gold-400"
                              : "bg-white/10 text-gray-400"
                          }`}
                        >
                          {order.status}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <a
                            href={`https://wa.me/${order.phone.replace(/\s/g, "")}?text=Merhaba%20${encodeURIComponent(order.customer)},%20${encodeURIComponent(order.product)}%20siparişiniz%20${encodeURIComponent(order.status.toLowerCase())}%20durumdadır.%20Detaylar%20için%20bizimle%20iletişime%20geçebilirsiniz.`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors cursor-pointer"
                            title="WhatsApp Gönder"
                          >
                            <i className="ri-whatsapp-line" />
                          </a>
                          <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 text-gray-400 hover:bg-white/10 transition-colors cursor-pointer" title="Düzenle">
                            <i className="ri-edit-line" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
